import {
  hashKey,
  useInfiniteQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import {
  ContentHubCursorError,
  ContentHubFilterError,
  searchContentHubByTree,
  searchContentHubEvents,
} from "./api";
import {
  CONTENT_HUB_SOURCES,
  contentHubEventsKey,
  contentHubSearchKey,
  type ContentHubSource,
} from "./config";
import {
  buildTree,
  normalizeFilter,
  type ContentHubFilter,
} from "./filter";
import { contentHubStreamManager } from "./streamManager";
import type {
  ContentHubPage,
  ContentHubStreamStatus,
} from "./types";

/** The opaque next-page cursor (a string) or undefined to stop paging. */
type PageParam = string | undefined;

const getNextPageParam = (last: ContentHubPage): PageParam =>
  last.nextCursor ?? undefined;

export interface UseContentHubEventsOptions {
  /** When false, neither the initial fetch nor the stream subscription runs. */
  enabled?: boolean;
}

/**
 * Historical + live events for one or more content-hub sources (a single-source
 * widget passes one source; a combined view passes several — the server merges
 * them into one newest-first, single-cursor stream).
 *
 * History comes from POST /v1/search, cursor-paginated (scroll to load older
 * pages); freshness comes from the shared /v1/stream SSE connection, whose
 * frames the stream manager patches into this query's pages — so the query
 * never goes stale and only resets when the stream reconnects.
 */
export function useContentHubEvents(
  sources: ContentHubSource | readonly ContentHubSource[],
  options?: UseContentHubEventsOptions
) {
  const enabled = options?.enabled ?? true;
  const queryClient = useQueryClient();

  const sourceList = useMemo(
    () => (typeof sources === "string" ? [sources] : [...sources]),
    [sources]
  );
  // Stable key derived from the (sorted) source list.
  const queryKey = useMemo(() => contentHubEventsKey(sourceList), [sourceList]);

  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => searchContentHubEvents(sourceList, pageParam),
    initialPageParam: undefined as PageParam,
    getNextPageParam,
    enabled,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    // No maxPages cap: pages are newest-first with only forward paging, so
    // capping would drop page one (the newest items + the stream prepend
    // target) with no way to refetch it. MAX_FIRST_PAGE bounds live growth.
  });

  useEffect(() => {
    if (!enabled) return;
    return contentHubStreamManager.subscribe(queryClient);
  }, [queryClient, enabled]);

  return query;
}

/**
 * Combined feed over several sources — a thin alias of useContentHubEvents that
 * pins the whole source list into a single merged, cursor-paginated query.
 */
export function useContentHubFeed(
  sources: readonly ContentHubSource[],
  options?: UseContentHubEventsOptions
) {
  return useContentHubEvents(sources, options);
}

export interface UseContentHubSearchOptions {
  /** When false the query does not run. */
  enabled?: boolean;
  /** Source AND-ed into every request as a `backend_source` leaf (e.g. the
   *  headlines list pins "newsquawk-headlines"). */
  pinnedSource?: ContentHubSource;
}

/**
 * Filtered content-hub search over POST /v1/search, cursor-paginated, with
 * live updates.
 *
 * The `filter` is compiled to a filter tree via `buildTree`; equivalent filters
 * share a cache entry (the query key uses `normalizeFilter`) and one live
 * server subscription. The filter is registered on the shared stream via
 * `/v1/stream/configure` (keyed by the query-key hash); matching events arrive
 * as `match` frames and the stream manager patches them into this cache — so
 * filtered views live-update exactly like the unfiltered feeds (staleTime
 * Infinity), including keyword filters matched server-side.
 *
 * Ordering: the query fetches page one only once its filter is settled
 * (configure acked, rejected, or the fallback timeout), so registration
 * precedes the history snapshot and no live events fall in the gap. A tree 422
 * is a `buildTree` bug (surfaced, never retried); a cursor 422 resets to page
 * one.
 */
export function useContentHubSearch(
  filter: ContentHubFilter,
  options?: UseContentHubSearchOptions
) {
  const enabled = options?.enabled ?? true;
  const pinnedSource = options?.pinnedSource;
  const queryClient = useQueryClient();

  const normalized = useMemo(() => normalizeFilter(filter), [filter]);
  const queryKey = useMemo(
    () => contentHubSearchKey(pinnedSource ?? null, normalized),
    [pinnedSource, normalized]
  );
  // The configure `id` is the query-key hash: identical filters share an id
  // (one server subscription), and `match` frame filter_ids route straight to
  // this cache. It's a value hash, so it's stable even if the caller rebuilds
  // an equivalent filter object each render.
  const filterId = useMemo(() => hashKey(queryKey), [queryKey]);
  const tree = useMemo(
    () => buildTree(filter, pinnedSource),
    // filterId already encodes normalized filter + pinnedSource by value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filterId]
  );

  // Register the live filter (and keep the socket alive) while enabled. Keyed on
  // filterId (a value hash) with tree/queryKey read via refs, so an equivalent
  // filter rebuilt each render doesn't churn the registration.
  const registrationRef = useRef({ tree, queryKey });
  registrationRef.current = { tree, queryKey };
  useEffect(() => {
    if (!enabled) return;
    const unsubSocket = contentHubStreamManager.subscribe(queryClient);
    const { tree: t, queryKey: qk } = registrationRef.current;
    const unregister = contentHubStreamManager.registerFilter(filterId, t, qk);
    return () => {
      unregister();
      unsubSocket();
    };
  }, [enabled, filterId, queryClient]);

  // Configure-first gate: fetch page one only once the filter has settled
  // (acked/errored/timed-out), so stream registration precedes the snapshot.
  const filterStatus = useSyncExternalStore(
    contentHubStreamManager.onFilterStatusChange,
    () => contentHubStreamManager.getFilterStatus(filterId)
  );
  const filterReady =
    !enabled /* no gating when disabled */ ||
    filterStatus === "ready" ||
    filterStatus === "errored";

  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => searchContentHubByTree(tree, pageParam),
    initialPageParam: undefined as PageParam,
    getNextPageParam,
    enabled: enabled && filterReady,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: (failureCount, error) =>
      error instanceof ContentHubFilterError ||
      error instanceof ContentHubCursorError
        ? false
        : failureCount < 3,
  });

  // A corrupted cursor (422 mid-walk) can't be retried — drop back to a fresh
  // page one. Page one carries no cursor, so this can't loop.
  const resettingRef = useRef(false);
  useEffect(() => {
    if (query.error instanceof ContentHubCursorError && !resettingRef.current) {
      resettingRef.current = true;
      queryClient.resetQueries({ queryKey }).finally(() => {
        resettingRef.current = false;
      });
    }
  }, [query.error, queryClient, queryKey]);

  return query;
}

export const useContentHubHeadlines = (options?: UseContentHubEventsOptions) =>
  useContentHubEvents(CONTENT_HUB_SOURCES.HEADLINES, options);

export const useContentHubTruthSocial = (
  options?: UseContentHubEventsOptions
) => useContentHubEvents(CONTENT_HUB_SOURCES.TRUTH_SOCIAL, options);

/** Live status of the shared /v1/stream connection. */
export function useContentHubStreamStatus(): ContentHubStreamStatus {
  return useSyncExternalStore(
    contentHubStreamManager.onStatusChange,
    contentHubStreamManager.getStatus
  );
}

/** The infinite-data shape these queries produce, for consumers/helpers. */
export type ContentHubInfiniteData = InfiniteData<ContentHubPage, PageParam>;
